// =============================================
// LISTA DE PAGOS - MEJORADA
// Con exportación, ordenamiento, paginación y total
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TableSortLabel,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import paymentService from '../../services/paymentService';
import userService from '../../services/userService';
import api from '../../services/api';
import PaymentForm from './PaymentForm';
import DepositForm from './DepositForm';
import ImageViewer from '../Common/ImageViewer';

const PaymentList = () => {
  // PAYMENTS: Estados
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [searchYearsBack, setSearchYearsBack] = useState(3);

  // DEPOSITS: Estados del modal de abonos
  const [openDepositForm, setOpenDepositForm] = useState(false);

  // FILTROS: Estados
  const [filters, setFilters] = useState({
    user_id: '',
    month: '',
    year: new Date().getFullYear(),
  });

  // ORDENAMIENTO: Estados
  const [orderBy, setOrderBy] = useState('payment_date');
  const [order, setOrder] = useState('desc');

  // PAGINACIÓN: Estados
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // PAYMENTS: Cargar datos al montar
  useEffect(() => {
    loadSystemParameters();
    loadPayments();
    loadUsers();
  }, []);


  // PAYMENTS: Cargar parámetros del sistema
  const loadSystemParameters = async () => {
    try {
      const response = await api.get('/parameters');
      const params = response.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');

      if (searchYearsParam) {
        setSearchYearsBack(parseInt(searchYearsParam.param_value) || 3);
      }
    } catch (err) {
      console.error('Error al cargar parámetros del sistema:', err);
      // Mantener el valor por defecto de 3
    }
  };

  // FILTROS Y ORDENAMIENTO: Aplicar cuando cambian (solo ordenamiento y pagos, filtros ahora son manuales)
  useEffect(() => {
    applyFiltersAndSort();
  }, [payments, orderBy, order]);

  // PAYMENTS: Cargar todos los pagos
  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentService.getAll();
      setPayments(response.payments || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  // USUARIOS: Cargar propietarios
  const loadUsers = async () => {
    try {
      const response = await userService.getAll();
      const owners = response.users?.filter(u => u.role === 'owner') || [];
      setUsers(owners);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  // FILTROS: Aplicar filtros y ordenamiento
  const applyFiltersAndSort = () => {
    let filtered = [...payments];

    // Filtrar por propietario
    if (filters.user_id) {
      filtered = filtered.filter(p => p.user_id === parseInt(filters.user_id));
    }

    // Filtrar por mes
    if (filters.month) {
      filtered = filtered.filter(p => p.month === parseInt(filters.month));
    }

    // Filtrar por año (incluir pagos con year NULL basándose en payment_date)
    if (filters.year) {
      filtered = filtered.filter(p => {
        // Si el pago tiene year NULL (abonos distribuidos), usar el año de payment_date
        if (p.year === null || p.year === undefined) {
          const paymentYear = new Date(p.payment_date).getFullYear();
          return paymentYear === parseInt(filters.year);
        }
        // Si tiene year, comparar directamente
        return p.year === parseInt(filters.year);
      });
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Para fechas
      if (orderBy === 'payment_date' || orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Para números
      if (orderBy === 'amount' || orderBy === 'month' || orderBy === 'year') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      // Para strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredPayments(filtered);
    setPage(0); // Reset página al aplicar filtros
  };

  // FILTROS: Cambiar filtro
  const handleFilterChange = (field, value) => {
    // Asegurar que el valor sea del tipo correcto
    let processedValue = value;
    if (field === 'month' || field === 'year') {
      processedValue = value === '' ? '' : parseInt(value);
    }

    setFilters({
      ...filters,
      [field]: processedValue
    });
    // Ya no reseteamos página aquí, sino al aplicar
  };

  // FILTROS: Limpiar filtros
  const handleClearFilters = () => {
    setFilters({
      user_id: '',
      month: '',
      year: new Date().getFullYear(),
    });
    // El usuario debe dar clic en Buscar para aplicar
  };

  // ORDENAMIENTO: Cambiar ordenamiento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // PAGINACIÓN: Cambiar página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // PAGINACIÓN: Cambiar filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // EXPORT: Exportar a Excel
  const handleExportToExcel = async () => {
    if (!filteredPayments || filteredPayments.length === 0) {
      setSnackbar({ open: true, message: 'No hay datos para exportar', severity: 'warning' });
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Pagos');

      // Columnas
      worksheet.columns = [
        { header: 'ID', key: 'id', width: 8 },
        { header: 'Propietario', key: 'user_name', width: 30 },
        { header: 'Casa', key: 'house_number', width: 12 },
        { header: 'Monto', key: 'amount', width: 15, style: { numFmt: '"B/."#,##0.00' } },
        { header: 'Mes', key: 'month', width: 12 },
        { header: 'Año', key: 'year', width: 10 },
        { header: 'Fecha de Pago', key: 'payment_date', width: 15 },
        { header: 'Fecha de Registro', key: 'created_at', width: 20 }
      ];

      // Estilo Encabezado
      const headerRow = worksheet.getRow(1);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF004D4D' }
        };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Datos
      filteredPayments.forEach((payment, index) => {
        const row = worksheet.addRow({
          id: payment.id,
          user_name: payment.user_name,
          house_number: payment.house_number,
          amount: parseFloat(payment.amount),
          month: getMonthName(payment.month),
          year: payment.year,
          payment_date: formatDate(payment.payment_date),
          created_at: formatDate(payment.created_at)
        });

        // Estilos de fila
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle' };

          if (index % 2 === 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF9F9F9' }
            };
          }
        });
      });

      // Agregar fila de totales
      const totalAmount = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const totalRow = worksheet.addRow({
        user_name: 'TOTAL',
        amount: totalAmount
      });

      totalRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0F2F1' }
        };
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `pagos_${new Date().toISOString().split('T')[0]}.xlsx`);

      setSnackbar({
        open: true,
        message: `Exportados ${filteredPayments.length} pagos a Excel`,
        severity: 'success',
      });

    } catch (err) {
      console.error('Error al exportar Excel:', err);
      setSnackbar({ open: true, message: 'Error al generar Excel', severity: 'error' });
    }
  };

  // PAYMENTS: Abrir formulario para crear
  const handleCreate = () => {
    setSelectedPayment(null);
    setOpenForm(true);
  };

  // PAYMENTS: Abrir formulario para editar
  const handleEdit = (payment) => {
    setSelectedPayment(payment);
    setOpenForm(true);
  };

  // PAYMENTS: Cerrar formulario
  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedPayment(null);
  };

  // PAYMENTS: Manejar éxito
  const handleSuccess = () => {
    loadPayments();
    setSnackbar({
      open: true,
      message: selectedPayment ? 'Pago actualizado exitosamente' : 'Pago registrado exitosamente',
      severity: 'success',
    });
  };

  // PAYMENTS: Eliminar pago
  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este pago?')) {
      try {
        await paymentService.delete(id);
        loadPayments();
        setSnackbar({
          open: true,
          message: 'Pago eliminado exitosamente',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error al eliminar pago',
          severity: 'error',
        });
      }
    }
  };

  // PAYMENTS: Ver comprobante
  const handleViewReceipt = (receiptUrl) => {
    if (!receiptUrl) return;

    let imageUrl;
    if (receiptUrl.startsWith('http')) {
      imageUrl = receiptUrl;
    } else if (receiptUrl.startsWith('/uploads/')) {
      // Usar la base URL del API pero removiendo '/api' si existe, o usar window.location.origin si es relativo al server
      // Asumiendo que las imagenes se sirven estáticamente desde el backend
      const apiBase = api.defaults.baseURL || '';
      // Si apiBase es http://localhost:3001/api, queremos http://localhost:3001
      const serverUrl = apiBase.replace(/\/api\/?$/, '');

      if (serverUrl.startsWith('http')) {
        imageUrl = `${serverUrl}${receiptUrl}`;
      } else {
        // Si es una ruta relativa (ej. /api), asumimos mismo origen
        imageUrl = receiptUrl;
      }
    } else {
      // Solo nombre de archivo, construir ruta
      const apiBase = api.defaults.baseURL || '';
      const serverUrl = apiBase.replace(/\/api\/?$/, '');
      imageUrl = `${serverUrl}/uploads/payments/${receiptUrl}`;
    }

    console.log('Abriendo imagen:', imageUrl);
    setSelectedImage(imageUrl);
    setImageViewerOpen(true);
  };

  // PAYMENTS: Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // DEPOSITS: Abrir formulario de abonos
  const handleOpenDepositForm = () => {
    setOpenDepositForm(true);
  };

  // DEPOSITS: Cerrar formulario de abonos
  const handleCloseDepositForm = () => {
    setOpenDepositForm(false);
  };

  // DEPOSITS: Éxito al aplicar abono
  const handleDepositSuccess = (summary) => {
    loadPayments();
    setSnackbar({
      open: true,
      message: `Abono aplicado: ${summary.payments_created} pagos creados (${summary.complete_months} completos, ${summary.partial_months} parciales)`,
      severity: 'success',
    });
  };

  // PAYMENTS: Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // PAYMENTS: Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    // Evitar conversión de zona horaria usando la fecha directamente
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  // PAYMENTS: Obtener nombre del mes
  const getMonthName = (month) => {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[month - 1] || '-';
  };

  // PAYMENTS: Calcular total
  const calculateTotal = () => {
    return filteredPayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  };

  // Obtener datos paginados
  const paginatedPayments = filteredPayments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* PAYMENTS: Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Pagos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportToExcel}
            disabled={filteredPayments.length === 0}
          >
            Exportar a Excel
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleOpenDepositForm}
          >
            Aplicar Abono
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Nuevo Pago
          </Button>
        </Box>
      </Box>

      {/* FILTROS: Panel de filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Filtros
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={applyFiltersAndSort}
              color="primary"
            >
              Aplicar Filtros
            </Button>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              variant="outlined"
            >
              Limpiar
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Filtro: Propietario */}
          <Grid item xs={12} sm={6} md={8}>
            <FormControl sx={{ width: 260 }}>
              <InputLabel>Propietario</InputLabel>
              <Select
                value={filters.user_id}
                label="Propietario"
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
              >
                <MenuItem value="" sx={{ width: 260 }}>Todos</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} {user.house_number ? `- Casa ${user.house_number}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtro: Mes */}
          <Grid item xs={12} sm={4} md={6}>
            <FormControl sx={{ width: 100 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={filters.month}
                label="Mes"
                onChange={(e) => handleFilterChange('month', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <MenuItem key={month} value={month}>
                    {getMonthName(month)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtro: Año */}
          <Grid item xs={12} sm={3} md={3}>
            <FormControl fullWidth>
              <InputLabel>Año</InputLabel>
              <Select
                value={filters.year}
                label="Año"
                onChange={(e) => handleFilterChange('year', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: searchYearsBack }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Contador de resultados */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            label={`${filteredPayments.length} ${filteredPayments.length === 1 ? 'pago' : 'pagos'}`}
            color="primary"
            size="small"
          />
          <Chip
            label={`Total: ${formatCurrency(calculateTotal())}`}
            color="success"
            size="small"
          />
        </Box>
      </Paper>

      {/* PAYMENTS: Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* PAYMENTS: Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* PAYMENTS: Tabla con ordenamiento */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'user_name'}
                      direction={orderBy === 'user_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('user_name')}
                    >
                      Propietario
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'house_number'}
                      direction={orderBy === 'house_number' ? order : 'asc'}
                      onClick={() => handleRequestSort('house_number')}
                    >
                      Casa
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'amount'}
                      direction={orderBy === 'amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('amount')}
                    >
                      Monto
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'month'}
                      direction={orderBy === 'month' ? order : 'asc'}
                      onClick={() => handleRequestSort('month')}
                    >
                      Mes
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'year'}
                      direction={orderBy === 'year' ? order : 'asc'}
                      onClick={() => handleRequestSort('year')}
                    >
                      Año
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'payment_date'}
                      direction={orderBy === 'payment_date' ? order : 'asc'}
                      onClick={() => handleRequestSort('payment_date')}
                    >
                      Fecha de Pago
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Comprobantes</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPayments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>{payment.id}</TableCell>
                    <TableCell>{payment.user_name}</TableCell>
                    <TableCell>{payment.house_number || '-'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="success.main">
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.month ? (
                        <Chip
                          label={getMonthName(payment.month)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="Abono"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {payment.year || '-'}
                    </TableCell>
                    <TableCell>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* Recibo Oficial Digital */}
                        {payment.digital_receipt_url && (
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => window.open(`http://localhost:3001${payment.digital_receipt_url}`, '_blank')}
                            title="Ver Recibo Oficial (PDF)"
                          >
                            <ReceiptIcon color="error" />
                          </IconButton>
                        )}
                        {/* Comprobante de Pago (Original) */}
                        {payment.payment_proof_url ? (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewReceipt(payment.payment_proof_url)}
                            title="Ver Comprobante Subido"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        ) : !payment.digital_receipt_url && (
                          <Typography variant="caption" color="text.secondary">
                            Sin adjuntos
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(payment)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(payment.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {/* FILA DE TOTAL */}
                {filteredPayments.length > 0 && (
                  <TableRow sx={{ bgcolor: 'grey.100', '& td': { fontWeight: 'bold' } }}>
                    <TableCell colSpan={3} align="right">
                      TOTAL ({filteredPayments.length} {filteredPayments.length === 1 ? 'pago' : 'pagos'}):
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="success.main" variant="h6">
                        {formatCurrency(calculateTotal())}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={5}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* PAGINACIÓN */}
            <TablePagination
              rowsPerPageOptions={[6, 12, 24, 48, 96]}
              component="div"
              count={filteredPayments.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>

          {/* PAYMENTS: Mensaje si no hay pagos */}
          {filteredPayments.length === 0 && !loading && (
            <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {filters.user_id || filters.month || filters.year
                  ? 'No se encontraron pagos con los filtros aplicados'
                  : 'No hay pagos registrados'}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* PAYMENTS: Formulario */}
      <PaymentForm
        open={openForm}
        onClose={handleCloseForm}
        payment={selectedPayment}
        onSuccess={handleSuccess}
      />

      {/* DEPOSITS: Formulario de abonos */}
      <DepositForm
        open={openDepositForm}
        onClose={handleCloseDepositForm}
        onSuccess={handleDepositSuccess}
      />

      {/* PAYMENTS: Visor de imágenes */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageUrl={selectedImage}
      />

      {/* PAYMENTS: Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentList;
